import React from 'react';
import { Paper, PaperProps } from '@mui/material';

/**
 * CRT terminal-style panel: a Paper with a floating // LABEL tag on its border.
 *
 * Spec (from ../jeeves/docs/style-guide.md):
 *   .panel { border: 1px solid var(--border); background: var(--bg-card); position: relative; }
 *   .panel::before {
 *     content: attr(data-label);
 *     position: absolute; top: -9px; left: 14px;
 *     background: var(--bg); padding: 0 8px;
 *     font-family: var(--font-head); font-size: 8px;
 *     color: var(--text-dim); letter-spacing: 0.1em;
 *   }
 */
interface PanelProps extends Omit<PaperProps, 'title'> {
  /** Label text that floats on the top border. Will be prefixed with "// " and uppercased. */
  label?: string;
}

const Panel: React.FC<PanelProps> = ({ label, children, sx, ...rest }) => {
  const dataLabel = label ? `// ${label.toUpperCase()}` : undefined;

  return (
    <Paper
      {...rest}
      data-label={dataLabel}
      sx={{
        position: 'relative',
        p: 3,
        overflow: 'visible',
        '&[data-label]::before': {
          content: 'attr(data-label)',
          position: 'absolute',
          top: '-9px',
          left: '14px',
          background: 'var(--crt-bg)',
          px: '8px',
          fontFamily: "'Press Start 2P', monospace",
          fontSize: '8px',
          color: 'var(--crt-dim)',
          letterSpacing: '0.1em',
          whiteSpace: 'nowrap',
          zIndex: 1,
        },
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
};

export default Panel;
